import { useMe } from "@nu-assist/shared-fe";
import { useQueryClient } from "@tanstack/react-query";
/**
 * <AuthGuard /> — wraps protected routes; redirects to /login when no
 * authenticated user is available.
 *
 * Session lifecycle (Phase 3): `useMe()` runs through the shared
 * session-enforcing `authedFetch`. An *expired access* cookie is
 * recovered transparently (single-flight `POST /auth/refresh` + retry),
 * so an active user keeps working without seeing /login. A *hard*
 * expiry (refresh cookie absent/invalid/expired) fires the injected
 * `onSessionExpired` below: it clears the TanStack Query cache (so no
 * stale user lingers) and replace-navigates to /login — the protected
 * route is unreachable after a hard refresh expiry. `useMe()` also
 * reports `no-user` in that case, so the existing redirect path is the
 * structural backstop even if the callback is a no-op.
 *
 * `AdvisoryClientProvider` is mounted here so every advisory API caller
 * in the protected tree shares ONE `makeAuthedFetch` instance wired to
 * the same `onSessionExpired` callback — a 401 from either surface
 * (nu-assist API or advisory API) triggers the same cache-clear + /login.
 *
 * Behavior per `useMe()` state:
 *   - `loading`: render the muted loading state + nothing else
 *   - `authenticated`: render children inside AdvisoryClientProvider
 *   - `no-user` / `error`: replace-navigate to /login
 *
 * Used like:
 *   <Route path="/dashboard" element={<AuthGuard><Dashboard /></AuthGuard>} />
 */
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AdvisoryClientProvider } from "../context/AdvisoryClientContext";
import { clearStoredSelectedImport } from "../features/imports/selection/SelectedImportContext";

/**
 * sessionStorage key holding the path a user tried to reach before being
 * bounced to /login. Read by AuthCallback after a successful sign-in so a
 * shared deep link (e.g. /research/imports?preview=1) lands where it was
 * intended. In sessionStorage (not React state) so it survives the OAuth
 * redirect round-trip.
 */
export const RETURN_TO_KEY = "nu-assist:return-to";

export function AuthGuard({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Keep a ref to the latest location so storeReturnTo / onSessionExpired
  // can read the current path without re-creating on every navigation.
  // This makes onSessionExpired stable across renders, which prevents
  // AdvisoryClientProvider from rebuilding its makeAuthedFetch instance
  // on every route change.
  const locationRef = useRef(location);
  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  // Persist the intended path (pathname + search) before any bounce so
  // AuthCallback can restore it. Stable — reads the latest location via ref.
  const storeReturnTo = useCallback(() => {
    const intended = locationRef.current.pathname + locationRef.current.search;
    if (!intended.startsWith("/login")) {
      sessionStorage.setItem(RETURN_TO_KEY, intended);
    }
  }, []); // stable: no location dependency, reads via ref

  // Shared session-expiry handler injected into both `useMe` (nu-assist
  // API) and `AdvisoryClientProvider` (advisory API). Fires exactly once
  // when the refresh token is gone — wipes every cached query and hard-
  // routes to /login so the protected tree unmounts immediately.
  // Stable across navigations — AdvisoryClientProvider's useMemo does not
  // re-run on every route change.
  const onSessionExpired = useCallback(() => {
    queryClient.clear();
    // Same rationale as the logout path in WebShellLayout: the persisted
    // import selection carries another recruiter's data and must not outlive
    // the session that chose it.
    clearStoredSelectedImport();
    storeReturnTo();
    navigate("/login", { replace: true });
  }, [queryClient, navigate, storeReturnTo]);

  const me = useMe({ onSessionExpired });

  useEffect(() => {
    if (me.status === "no-user" || me.status === "error") {
      storeReturnTo();
      navigate("/login", { replace: true });
    }
  }, [me.status, navigate, storeReturnTo]);

  if (me.status === "loading") {
    return (
      <div className="min-h-screen bg-bg p-6 font-sans">
        <p className="text-text-muted text-body">Loading…</p>
      </div>
    );
  }

  if (me.status !== "authenticated") {
    // The useEffect above is firing a navigation; render nothing while
    // the redirect happens. (Returning null avoids a flash of /login
    // content under /dashboard's URL.)
    return null;
  }

  return (
    <AdvisoryClientProvider
      apiBase={import.meta.env.VITE_API_URL ?? "https://api.dev.nu-assist.com"}
      onSessionExpired={onSessionExpired}
    >
      {children}
    </AdvisoryClientProvider>
  );
}
